import { Component, OnInit, ViewEncapsulation, Input, Output, EventEmitter, ViewChild, ElementRef, PLATFORM_ID, Inject } from '@angular/core';

import { ReplaySubject, Observable, empty } from 'rxjs';
import { throttleTime, first } from 'rxjs/operators';

import { Media, AudioProgressEv, MediaEventType, MediaState, Mote, MoteEventType, MediaEvent, AudioSource, AudioItemId, AudioItemPosition } from '../../models';
import { GyroService, FetchService, AutoSub, AutoSubOnDestroy, MapService, MapZoomState, MoteEventService } from '../../providers';
import { MotesMediaPlayerService } from '../../motes-media-player/media-player.service';
import { ProgressManager } from '../../models/progress-manager';

import { AudioItem, AudioItemEventType, AudioItemAmbisonicId } from '../../models';
import { AmbisonicPlayerInterface } from './ambisonic-player.interface';

import { DeviceDetectorService } from 'ngx-device-detector';
import { MiniWorldComponent } from '../mini-world/mini-world.component';
import { isPlatformBrowser } from '@angular/common';

/**
 * Class: AugmentedPlayerComponent 
 * Augmented 3D sound player component wrapping ambisonic player
 * emmitating events of HTML Audio Element 
 */
@AutoSubOnDestroy()
@Component({
    selector: 'augmented-player',
    templateUrl: './augmented-player.component.html',
    styleUrls: ['./augmented-player.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AugmentedPlayerComponent implements OnInit {
    /**
     * Media object to play (load ambisonic source)
     */
    // @Input() set media(media: Media) {
    //     // this.loadMedia(media).then(() => {
    //     //     this.mediaPlayerService.augmentedPlayer = this;
    //     //     // Mark as playing (as the load media might marked it as stopped on second media load)
    //     //     this.player.setPlayingState(true);
    //     //     // Notify listners about init
    //     //     this.initSubject.next(true);
    //     // });
    // }

    // Container for adding AudioHtmlElements (debug) 
    @ViewChild('augmentedContainer', { static: true }) augmentedContainer: ElementRef<HTMLDivElement>;

    /**
     * Replicate audio element output
     */
    @Output() progress = new EventEmitter<AudioProgressEv>();
    @Output() timeupdate = new EventEmitter<AudioProgressEv>();
    @Output() canplay = new EventEmitter<AudioProgressEv>();
    @Output() ended = new EventEmitter<AudioProgressEv>();

    private readonly SceneDiameter = 20;
    private initSubject = new ReplaySubject<boolean>(1);

    public status: string = "Please wait, loading sound...";
    public emulatedUserRotation: boolean = false;

    private downloadManager = new ProgressManager();
    private player: AmbisonicPlayerInterface;

    private requestedMediaState: MediaState = MediaState.NONE;
    private media: Media = null;

    public itemBlobList: AudioItem[] = [];

    constructor(
        private mediaPlayerService: MotesMediaPlayerService,
        private gyroService: GyroService,
        private fetchService: FetchService,
        private mapService: MapService,
        private moteEventService: MoteEventService,
        private deviceService: DeviceDetectorService,
        @Inject(PLATFORM_ID) platform: Object
    ) {
        this.init(platform);
    }

    private async init(platform: Object) {
        // Dynamically import Ambisonics player as resonance-audio is not compatible with SSR build
        let amb = await (isPlatformBrowser(platform) ? import('./ambisonic-player') : import('./ambisonic-player.interface'));
        this.player = new amb.AmbisonicPlayer(this.SceneDiameter);
    }

    public awaitInit() {
        return this.initSubject.asObservable().pipe(first()).toPromise();
    }

    ngOnInit() {
        this.player.init();

        AutoSub(this).reg['audioTime'] = this.player.watchTimeEvents().subscribe((ev) => {
            this.emitTimeUpdateEvent(ev.currentTime, ev.duration);
        });

        AutoSub(this).reg['audioEnded'] = this.player.watchAudioEnded().subscribe(() => {
            this.emitEndedEvent();
        });

        AutoSub(this).reg['gyro'] = this.gyroService.watchGyro().pipe(throttleTime(100)).subscribe((ev) => {
            if(this.emulatedUserRotation) return;
            this.player.setUserRotation(ev.hdgAbsolute);
        });

        AutoSub(this).reg['mediaEvents'] = this.mediaPlayerService.watchMediaEvents().subscribe(async (ev) => {

            if(ev.type == MediaEventType.PRELOAD_MEDIA) {
                this.initMedia(ev, false);
                return;
            }
            if(ev.type == MediaEventType.SEEK) {
                return this.player.seekAudio(ev.seekSec);
            }
            if(ev.type == MediaEventType.SET_CURRENT_TIME) {
                return this.player.setCurrentTime(ev.seekSec);
            }
            if(ev.type != MediaEventType.STATEUPDATE) return;

            this.requestedMediaState = ev.state;

            // console.log("Got media event ", ev);
            switch(ev.state) {
                case MediaState.PLAYING:
                    let deviceOk = await this.checkDeviceSupport();
                    if(!deviceOk) {
                        this.mediaPlayerService.reportVersionError();
                        return;
                    }

                    let playNow = await this.initMedia(ev);
                    if(!playNow) return;

                    let canPlay = await this.player.canPlay();
                    if(!canPlay) {
                        console.warn("Player can not start");
                        this.mediaPlayerService.reportPlayError();
                        return false;
                    }

                    // Await activation, then play
                    this.enable3DMode(ev.mote).then(() => {
                        this.player.play().catch(err => {
                            console.log("Could not start player (maybe play wasn't called from active user interaction context)");
                        });
                    });
                    break;

                case MediaState.PAUSED:
                    await this.awaitInit();
                    this.player.stop();
                    break;

                case MediaState.NONE:
                    await this.awaitInit();
                    this.player.stop();
                    this.disable3DMode(ev.mote).then(() => {
                        // Notify listners that 3D mode was disabled 
                        this.moteEventService.emitMoteEvent({ type: MoteEventType.HIDE_3D_MOTE_ON_MAP, mote: ev.mote });
                    });
                    break;
            }
        });

        AutoSub(this).reg['audioItemEvents'] = this.mediaPlayerService.watchAudioItemEvents().subscribe(async (ev) => {
            await this.awaitInit();
            switch(ev.type) {
                case AudioItemEventType.MOVE:
                    this.player.setItemPosition(ev.target, ev.position);
                    break;

                case AudioItemEventType.ENABLE:
                    this.player.enableItem(ev.target, true);
                    break;

                case AudioItemEventType.DISABLE:
                    this.player.enableItem(ev.target, false);
                    break;

                case AudioItemEventType.ROTATE:
                    this.emulatedUserRotation = true;
                    this.player.setUserRotation(ev.position.rotation);
                    break;
            }
        });
    }

    private async initMedia(ev: MediaEvent, autoPlay: boolean = true): Promise<boolean> {
        if(this.media && this.media.key == ev.media.key) {
            // Media was already loaded, play that audio now
            return true;
        }
        this.media = ev.media;

        // Load the files to play
        await this.loadMedia(ev.media);
        // Mark media initialized
        this.initSubject.next(true);

        let canPlay = await this.player.canPlay();
        if(!canPlay) {
            // Postpone playing (wait for next click before starting to avoid permission error on iOS)
            this.mediaPlayerService.mediaLoaded(ev.mote, ev.media);
        }
        return canPlay && autoPlay;
    }

    private async checkDeviceSupport(): Promise<boolean> {
        let deviceInfo = this.deviceService.getDeviceInfo();
        // Always return true on any non iOS device
        if(deviceInfo.os != "iOS") return true;
        // We only support latest iOS version, >= 13.6 
        let version = this.getIosVersion(deviceInfo.userAgent);
        await this.gyroService.requestPermissions();
        // if(version.major >= 13 && version.minor >= 6) return true;
        // Always return false on iOS devices for now
        return false;
    }

    private getIosVersion(userAgent: string): { major: number, minor: number, patch: number } {
        const parsedVersion = userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
        return {
            major: parseInt(parsedVersion[1] || '0', 10),
            minor: parseInt(parsedVersion[2] || '0', 10),
            patch: parseInt(parsedVersion[3] || '0', 10)
        }
    }

    /**
     * Directly start playing media (normally this will be started through media events, but this function allow us to play on iOS as well)
     */
    private play(): Promise<void[]> {
        // Mark as playing (as the load media might marked it as stopped on second media load)
        this.player.setPlayingState(true);
        return this.player.play();
    }

    private emitProgressEvent(loaded: number, duration: number = 10000) {
        let ranges: TimeRanges = { length: 1, start: (idx) => 0, end: (idx) => loaded };
        this.progress.emit({ srcElement: { buffered: ranges, currentTime: 0, duration: duration } });
    }

    private emitTimeUpdateEvent(currentTime: number, duration: number) {
        let ranges: TimeRanges = { length: 1, start: (idx) => 0, end: (idx) => duration };
        this.timeupdate.emit({ srcElement: { buffered: ranges, currentTime: currentTime, duration: duration } });
    }

    private emitEndedEvent() {
        this.ended.emit();
    }

    private enable3DMode(mote: Mote): Promise<void> {
        return new Promise(resolve => {
            let position = mote.position.longLat();
            // TODO: Store this in Mote... for now just use generic zoom levels
            // Consider handling this from tab-bar like other zoom related map stuff
            const zoomAnimation: MapZoomState[] = [
                { center: position, zoom: 15.0, bearing: this.mapService.map.getBearing(), pitch: 5, duration: 0, delayEndEvents: 1000, offset: [240, -720] },
                // { center: position, zoom: 16.0, bearing: 250, pitch: 60, duration: 2000, delayEndEvents: 500 },
                { center: position, zoom: 16, bearing: mote.rotation, pitch: mote.rotation, duration: 1000, delayEndEvents: 1000 },
                { center: position, zoom: 17.6, bearing: mote.rotation, pitch: 60, duration: 3000, offset: [-60, 180] }
            ]

            this.mapService.enable3DMode(zoomAnimation).then(async (activated) => {
                // Do nothing if 3D mode was already activated
                if(!activated) return resolve();
                // Wait at least 3 sec before showing map (to show progress and let map relax a bit for better animation performance)
                await new Promise(resolve => setTimeout(resolve, 3500));
                // If user cancled in the mean while, do nothing
                if(this.requestedMediaState != MediaState.PLAYING) return resolve();

                // Show map view (before playing zoom animation)
                this.moteEventService.emitMoteEvent({ type: MoteEventType.SHOW_3D_MOTE_ON_MAP, mote: mote });
                // Play the zoom animation
                await this.mapService.playZoomAnimation(zoomAnimation);
                this.moteEventService.emitMoteEvent({ type: MoteEventType.ZOOM_TO_3D_MOTE_DONE, mote: mote });
                resolve();
            });
        });
    }

    private disable3DMode(mote: Mote): Promise<boolean> {
        let position = mote.position.longLat();

        // TODO: Store this in Mote... for now just use generic zoom levels
        const zoomAnimation: MapZoomState[] = [
            { center: position, zoom: 17.6, bearing: this.mapService.map.getBearing(), pitch: 60, duration: 0, offset: [-60, 180] },
            { center: position, zoom: 15.5, bearing: 140, pitch: 5, duration: 2000, delayEndEvents: 500 }
        ]
        return this.mapService.disable3DMode(zoomAnimation);
    }

    private async loadMedia(media: Media): Promise<void> {
        if(!media) return;

        // Delete old map
        this.player.stop();
        this.downloadManager.init();
        // Load audio files from media if any (make a copy of item list)
        let audioItemList = media ? media.augmentedAudioList.slice() : [];
        // Add Ambisonic file to the mix if any
        if(media.ambisonic.url) {
            let ambisonic: AudioItem = {
                id: AudioItemAmbisonicId,
                audioUrl: media.ambisonic.url,
                enabled: true,
                position: { xn: 0.5, yn: 0.5 }
            };
            audioItemList.push(ambisonic);
        }

        // Mark source loaded if no ambisonic file present
        AutoSub(this).reg['downloadProgress'] = this.downloadManager.watchCachingProgress().subscribe((res) => {
            //console.log("Got progress update ", res);
            // Emulate progress (as if the file was 15 sec long)
            this.emitProgressEvent((res.totalPct / 100) * 15, 15);
        });

        // Preload all audio files (strict audio timing is needed for ambisonic file playback)
        this.itemBlobList = await this.fetchSounds(audioItemList);

        // Add additional audio sources to play in augmented sphere (and load async)
        let audioSourceList = this.player.loadAudioSourceList(this.itemBlobList);

        // Insert debug HtmlAudioElements 
        // for(let item of audioSourceList) {
        //     this.augmentedContainer.nativeElement.insertAdjacentHTML('beforeend', '<div class="two"> Hello '+item.id+'</div>');
        //     this.augmentedContainer.nativeElement.appendChild(item.audioElement);
        // }

        // let test = range(1,20).pipe( zip(interval(1000), (a, b) => { console.log("Emulate progress ", a); this.emitProgressEvent(a, 20)}))
        // AutoSub(this).reg['progressTest'] = test.subscribe();
    }

    private fetchSounds(itemList: AudioItem[]): Promise<AudioItem[]> {
        if(!itemList) return Promise.resolve([]);
        return new Promise(async (resolve) => {
            // Init audio item download
            let group = this.downloadManager.initResourceGroup("AudioItems");

            // Add resources to list
            let blobResource = itemList.map(item => {
                let [rsp, resource] = this.fetchService.get(item.audioUrl, group.id, item.id, false);
                // Await response, get blob and create local blob url
                let awaitItemBlob = new Promise<AudioItem>(async (resolve) => {
                    let data = await rsp;
                    let blob = await data.blob();
                    // Create local url from blob
                    item.blobUrl = URL.createObjectURL(blob);
                    resolve(item);
                });
                return { blob: awaitItemBlob, resource: resource };
            });

            // Extract resource list
            group.resourceList = blobResource.map((entry) => entry.resource);
            // Extract response list
            let responseList = blobResource.map((entry) => entry.blob);

            // Start watching download progress (and updating audio buffer progress)
            this.downloadManager.start(group);
            let resolvedItems = await Promise.all(responseList);
            resolve(resolvedItems);
        });
    }

    ngOnDestroy() {
        this.player.destroy();
    }
}
